import {
  Button,
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';

import {
  useAllowDefaultBankTransferMutation,
  useDisallowDefaultBankTransferMutation,
  useGetBankTransferSettingQuery,
  useUpdateBankTransferSettingMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useTenant} from '@dms/shared';

import {
  BankTransferSettingsFormSchema,
  BankTransferSettingsFormValues,
} from '../schemas/BankTransferSettingsFormSchema';

export function BankTransferSettingsForm() {
  const tenant = useTenant();
  const {data, isLoading, isError} = useGetBankTransferSettingQuery();
  const [allowDefault] = useAllowDefaultBankTransferMutation();
  const [disallowDefault] = useDisallowDefaultBankTransferMutation();
  const [updateSettings] = useUpdateBankTransferSettingMutation();

  const isEnabled = data?.isDefaultBankTransferAllowed ?? false;

  const handleEnableClick = () => {
    allowDefault({}).unwrap().catch(handleApiError);
  };

  const handleDisableClick = () => {
    disallowDefault({}).unwrap().catch(handleApiError);
  };

  const handleSubmit: FormSubmitHandler<BankTransferSettingsFormValues> = (values) =>
    updateSettings({
      body: {
        invoiceMaturity: values.invoiceMaturity,
        paymentLimit: values.paymentLimit,
      },
    })
      .unwrap()
      .then(() => {
        showNotification.success();
      })
      .catch(handleApiError);

  return (
    <DataStatus isError={isError} isLoading={isLoading}>
      <VStack spacing={6}>
        <Heading size={4}>{i18n.t('entity.settings.bankTransfer.labels.formTitle')}</Heading>
        <Text color="secondary">{i18n.t('entity.settings.bankTransfer.formDescription')}</Text>

        <Box width={50} maxWidth={50}>
          <Show when={isEnabled}>
            <Button
              variant="secondary"
              onClick={handleDisableClick}
              title={i18n.t('entity.settings.bankTransfer.labels.disableDefault')}
            />
          </Show>
          <Show when={!isEnabled}>
            <Button
              variant="primary"
              onClick={handleEnableClick}
              title={i18n.t('entity.settings.bankTransfer.labels.enableDefault')}
            />
          </Show>
        </Box>

        <Show when={isEnabled}>
          <Form
            experimentalZodSchema={BankTransferSettingsFormSchema}
            onSubmit={handleSubmit}
            defaultValues={{
              invoiceMaturity: data?.invoiceMaturity ?? 0,
              paymentLimit: data?.paymentLimit ?? DEFAULT_PAYMENT_LIMIT,
            }}
            shouldWatchForUnsavedChanges
          >
            {(control, _formApi, navigateWithoutBlocker) => (
              <VStack spacing={4}>
                <HStack spacing={4}>
                  <Box width={100} maxWidth={100}>
                    <FormField
                      control={control}
                      name="invoiceMaturity"
                      type="integer"
                      isRequired
                      label={i18n.t('entity.settings.bankTransfer.labels.invoiceMaturity')}
                    />
                  </Box>
                  <Box width={100} maxWidth={100}>
                    <FormField
                      isRequired
                      control={control}
                      name="paymentLimit"
                      type="currency"
                      label={i18n.t('entity.settings.bankTransfer.labels.paymentLimit')}
                      currency={tenant.tenantCurrency}
                      decimalPlaces={0}
                    />
                  </Box>
                </HStack>

                <SettingsFooter
                  actions={[
                    {
                      title: i18n.t('general.actions.discard'),
                      variant: 'secondary',
                      onClick: () => navigateWithoutBlocker(settingsRoutes.bankTransferSettings),
                      type: 'button',
                    },
                    {
                      type: 'form-button',
                      control,
                      buttonType: 'submit',
                      title: i18n.t('general.actions.save'),
                    },
                  ]}
                />
              </VStack>
            )}
          </Form>
        </Show>
      </VStack>
    </DataStatus>
  );
}

const DEFAULT_PAYMENT_LIMIT = 1_000_000;
